import { InputImageElement } from './esc-pos-image.types';

/**
 * Utility function to ensure images are fully loaded before processing
 */
export class ImageLoader {
  /**
   * Load an image from URL and return a promise with the loaded HTMLImageElement
   */
  static loadFromUrl(url: string): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      const img = new Image();

      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error(`Failed to load image from ${url}`));

      // Enable CORS if needed
      img.crossOrigin = 'anonymous';
      img.src = url;
    });
  }

  /**
   * Wait for an HTMLImageElement to be fully loaded
   */
  static waitForLoad(img: HTMLImageElement): Promise<HTMLImageElement> {
    return new Promise((resolve, reject) => {
      if (img.complete && img.naturalWidth > 0) {
        // Image is already loaded
        resolve(img);
        return;
      }

      const onLoad = () => {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
        resolve(img);
      };

      const onError = () => {
        img.removeEventListener('load', onLoad);
        img.removeEventListener('error', onError);
        reject(new Error('Image failed to load'));
      };

      img.addEventListener('load', onLoad);
      img.addEventListener('error', onError);
    });
  }

  /**
   * Ensure any input image element is ready for processing
   */
  static async ensureLoaded(element: InputImageElement): Promise<InputImageElement> {
    if (element instanceof HTMLImageElement) {
      await this.waitForLoad(element);
      return element;
    }

    // HTMLCanvasElement and ImageData are always "loaded"
    return element;
  }

  /**
   * Validate that an image element has valid dimensions
   */
  static validateImageDimensions(element: InputImageElement): void {
    let width: number;
    let height: number;

    if (element instanceof HTMLImageElement) {
      width = element.naturalWidth || element.width;
      height = element.naturalHeight || element.height;
    } else if (element instanceof HTMLCanvasElement) {
      width = element.width;
      height = element.height;
    } else if ('width' in element && 'height' in element) {
      width = element.width;
      height = element.height;
    } else {
      throw new Error('Invalid image element');
    }

    if (width === 0 || height === 0) {
      throw new Error('Image has invalid dimensions (0x0). Make sure the image is fully loaded.');
    }
  }
}
